/**
 * Keyboard Game - Challenge Modal Component
 * Copyright (c) 2025 All Rights Reserved
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Trophy, Zap, Target, Clock } from 'lucide-react';

export default function ChallengeModal({ open, challenge, onAccept, onDecline }) {
  if (!challenge) return null;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getModeDisplay = (mode) => {
    const modes = {
      words: 'Words',
      quote: 'Quote',
      trivia: 'Trivia',
      sprint: 'Sprint'
    };
    return modes[mode] || mode;
  };
  
  const getDifficultyDisplay = (difficulty) => {
    const difficulties = {
      normal: 'One Jumble',
      hard: 'Multi Jumble',
      easy: 'Easy',
      insane: 'Insane'
    };
    return difficulties[difficulty] || difficulty;
  };
  
  return (
    <Dialog open={open} onOpenChange={onDecline}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Challenge Invitation!
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-primary">{challenge.challenger_username}</span> challenges you to beat their score!
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Challenge Info */}
          <Card className="p-4 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mode</span>
                <span className="font-semibold">{getModeDisplay(challenge.mode)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Difficulty</span>
                <span className="font-semibold">{getDifficultyDisplay(challenge.difficulty)}</span>
              </div>
              
              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Zap className="w-4 h-4" />
                    <span>WPM</span>
                  </div>
                  <span className="text-lg font-bold text-blue-500">{Math.round(challenge.wpm)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Target className="w-4 h-4" />
                    <span>Accuracy</span>
                  </div>
                  <span className="text-lg font-bold text-green-500">{Math.round(challenge.accuracy)}%</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Time</span>
                  </div>
                  <span className="text-lg font-bold text-purple-500">{formatTime(challenge.time_seconds)}</span>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onDecline}
              className="flex-1"
            >
              Maybe Later
            </Button>
            <Button
              onClick={onAccept}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              Accept Challenge!
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
